import angular from 'angular';
import { localStorage } from '../utils';
import { v4 as uuidv4 } from 'uuid';
import '../services/socket';

const module = angular.module('whyline.factories.socket', [
  'whyline.services.socket'
]);

class WebSocketWrapper {

  constructor(url, options = {}) {
    this.url = url;
    this.options = Object.assign(
      {
        reconnectionAttempts: 5,
        reconnectInterval: 5000,
        heartbeatInterval: 60000,
        autoReconnectInterval: 3600000,
      },
      options
    );

    this.eventListeners = {};
    this.reconnectAttempts = 0;
    this.isReconnecting = false;
    this.replacingSocket = false;
    this.controlledReconnection = false;

    this.connect();
    
  }

  connect() {
    let newSocket = null;
    let oldSocket = null;
    if(!this.socket){
      this.socket = new WebSocket(this.url);
    }else{
      oldSocket = this.socket;
      newSocket = new WebSocket(this.url);
      oldSocket.close();
      this.socket = newSocket;
    }
    this.socket.addEventListener('open', this.handleOpen.bind(this));
    this.socket.addEventListener('message', this.handleMessage.bind(this));
    this.socket.addEventListener('error', this.handleError.bind(this));
    this.socket.addEventListener('close', this.handleClose.bind(this));
  }

  handleOpen(event) {
    console.log(`Socket: connected at ${new Date().toISOString()}`);
    const wasReconnecting = this.isReconnecting;
    this.isReconnecting = false;
    this.reconnectAttempts = 0;

    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }
    this.heartbeatTimer = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ type: 'heartbeat', timestamp: Date.now() }));
      }
    }, this.options.heartbeatInterval);

    if (this.autoReconnectTimer) {
      clearTimeout(this.autoReconnectTimer);
    }

    this.autoReconnectTimer = setTimeout(() => {
      console.log('Socket: auto-reconnect');
      this.controlledReconnection = true;
      this.connect();
    }, this.options.autoReconnectInterval);

    if (wasReconnecting) {
      this.triggerEvent('reconnect');
    } else {
      this.triggerEvent('connect');
    }
  }

  handleMessage(event) {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch (err) {
      console.error('Error parsing message:', err);
      return;
    }
    this.triggerEvent('socket:income-socket-message', data);
  }

  handleError(error) {
    console.error('Socket error:', error);
    // Handle error if needed
  }

  handleClose() {
    console.log(`Socket: disconnected at ${new Date().toISOString()}`);

    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }
    if (this.autoReconnectTimer) {
      clearTimeout(this.autoReconnectTimer);
    }

    if(this.controlledReconnection){
      this.controlledReconnection = false;
      return;
    }

    if (this.replacingSocket) {
      this.replacingSocket = false;
      console.log('Socket: old socket closed during auto-reconnect');
      return;
    }

    this.triggerEvent('disconnect');

    if (!this.isReconnecting && this.reconnectAttempts < this.options.reconnectionAttempts) {
      this.isReconnecting = true;
      this.reconnectAttempts += 1;
      setTimeout(() => {
        console.log(`Socket: attempting to reconnect (attempt ${this.reconnectAttempts})`);
        this.connect();
      }, this.options.reconnectInterval);
    } else {
      console.log('Socket: reconnection attempts exceeded');
      this.triggerEvent('reconnect_failed');
    }
  }

  autoReconnect() {
    return null;
    console.log('Socket: starting auto-reconnect');

    const oldSocket = this.socket;
    const newSocket = new WebSocket(this.url);

    newSocket.addEventListener('open', () => {
      console.log('Socket: new connection opened during auto-reconnect');

      this.socket = newSocket;

      this.triggerEvent('socket_new_connection', this.socket)

      if (this.heartbeatTimerTwo) {
        clearInterval(this.heartbeatTimerTwo);
      }

      this.heartbeatTimerTwo = setInterval(() => {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          this.socket.send(JSON.stringify({ type: 'heartbeat', timestamp: Date.now() }));
        }
      }, this.options.heartbeatInterval);

      if (this.autoReconnectTimerTwo) {
        clearTimeout(this.autoReconnectTimerTwo);
      }

      this.autoReconnectTimerTwo = setTimeout(() => {
        console.log('Socket: auto-reconnect');
        this.autoReconnect();
      }, this.options.autoReconnectInterval);

      if (oldSocket) {
        // Indicate that we're replacing the socket
        this.replacingSocket = true;
        oldSocket.close();
      }

    });
  }

  on(eventName, callback) {
    if (!this.eventListeners[eventName]) {
      this.eventListeners[eventName] = [];
    }
    this.eventListeners[eventName].push(callback);
  }

  off(eventName, callback) {
    if (!this.eventListeners[eventName]) return;
    const index = this.eventListeners[eventName].indexOf(callback);
    if (index !== -1) {
      this.eventListeners[eventName].splice(index, 1);
    }
  }

  triggerEvent(eventName, data) {
    const listeners = this.eventListeners[eventName];
    if (listeners) {
      listeners.forEach((callback) => callback(data));
    }
  }

  emit(eventName, data) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const message = JSON.stringify({ event: eventName, data });
      this.socket.send(message);
    } else {
      console.warn('Socket is not open. Unable to send message:', eventName);
    }
  }

  close() {
    if (this.socket) {
      this.socket.close();
    }
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }
    if (this.autoReconnectTimer) {
      clearTimeout(this.autoReconnectTimer);
    }
  }
}

const SocketFactory = ($rootScope, $q, $timeout, SocketService, SOCKET_URL, $http) => {
  'ngInject';
  const defer = $q.defer();
  const sessionCode = uuidv4();
  sessionStorage.setItem('intercom-socket-session', sessionCode);
  const connect = (token, opts) => {
    opts = Object.assign({
      reconnectionAttempts: 5,
      reconnectInterval: 5000,
      heartbeatInterval: 60000,
      autoReconnectInterval: 3600000,
    }, opts || {});
    
    const WS_URL = `${SOCKET_URL}?auth_key=${token}&entity_id=${"generic"}&type=GENERALPROPOUSE&sessionCode=${sessionCode}`;

    const newSocket = new WebSocketWrapper(WS_URL, opts);

    newSocket._id = Math.random();

    return newSocket;
  };

  $rootScope.$on('authenticated', () => {
    $timeout(() => {
      SocketService.GetInstance().then(currSocket => {
        if (currSocket) {
          return defer.resolve(currSocket)
        }

        const token = localStorage.get('token');

        const newSocket = connect(token);

        SocketService.SetInstance(newSocket);

        newSocket.on('connect', () => {
          console.log(`Socket: connected at ${new Date().toISOString()}`);
          $rootScope.$emit("socket-ready", {});
          SocketService.SetConnected(true);
        });

        newSocket.on('disconnect', () => {
          console.log(`Socket: disconnected at ${new Date().toISOString()}`);
          SocketService.SetConnected(false);
        });

        newSocket.on('reconnect', () => {
          console.log(`Socket: reconnected at ${new Date().toISOString()}`);
          SocketService.SetConnected(true);
        });

        newSocket.on('socket_new_connection', (instance) => {
          console.log(`Socket: Instance updated at ${new Date().toISOString()}`);
          SocketService.SetConnected(true);
          //SocketService.SetInstance(instance)
        });

        newSocket.on('reconnect_failed', () => {
          console.log(`Socket: reconnection failed at ${new Date().toISOString()}`);
          SocketService.SetConnected(false);
        });

        defer.resolve(newSocket);
      })
    })
  });

  $rootScope.$on('connect-display-socket', () => {
    $timeout(() => {
      const token = 'token_display';

      const newSocket = connect(token, { reconnectionAttempts: Infinity });

      SocketService.SetDisplayInstance(newSocket);

      newSocket.on('connect', () => {
        $rootScope.$emit('socket:display-connected', newSocket);
      });

      newSocket.on('disconnect', () => {
        $rootScope.$emit('display:disconnected');
      });

      newSocket.on('reconnect', () => {
        $rootScope.$emit('display:reconnected');
      });

      defer.resolve(newSocket);
    });
  });

  $rootScope.$on('connect-totem-socket', () => {
    $timeout(() => {
      const token = 'token_totem';

      const newSocket = connect(token, { reconnectionAttempts: Infinity });

      SocketService.SetTotemInstance(newSocket);

      newSocket.on('connect', () => {
        $rootScope.$emit('socket:totem-connected', newSocket);
      });

      newSocket.on('disconnect', () => {
        $rootScope.$emit('totem:disconnected');
      });

      newSocket.on('reconnect', () => {
        $rootScope.$emit('totem:reconnected');
      });

      defer.resolve(newSocket);
    });
  });

  return defer.promise;
};

module.exports = module.factory('Socket', SocketFactory);